// src/pages/GetStartedPage.tsx

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

// the bezel
import phoneFrame from '../assets/phone-frame.png';
import GS1 from '../assets/get-started-1.png';
import GS2 from '../assets/get-started-2.png';
import GS3 from '../assets/get-started-3.png';
import MOT1 from '../assets/MOT-step-1.png';
import MOT2 from '../assets/MOT-step-2.png';
import MOT3 from '../assets/MOT-step-3.png';
import MOT4 from '../assets/MOT-step-4.png';
import SR1 from '../assets/services-repairs-1.png';
import SR2 from '../assets/services-repairs-2.png';
import SR3 from '../assets/services-repairs-3.png';
import SR4 from '../assets/services-repairs-4.png';
import SR5 from '../assets/services-repairs-5.png';
import MA1 from '../assets/availability-1.png';
import MA2 from '../assets/availability-2.png';
import EX1 from '../assets/extras-1.png';
import EX2 from '../assets/extras-2.png';
import AU1 from '../assets/add-users-1.png';
import AU2 from '../assets/add-users-2.png';
import MB1 from '../assets/manage-bookings-1.png';
import MB2 from '../assets/manage-bookings-2.png';
import MB3 from '../assets/manage-bookings-3.png';
import MB4 from '../assets/manage-bookings-4.png';
import MB5 from '../assets/manage-bookings-5.png';
import MC1 from '../assets/manage-colleagues-1.png';
import MC2 from '../assets/manage-colleagues-2.png';
import MC3 from '../assets/manage-colleagues-3.png';
import IS1 from '../assets/invoice-summary-1.png';
import IS2 from '../assets/invoice-summary-2.png';
import IS3 from '../assets/invoice-summary-3.png';
import I1 from '../assets/invoices-1.png';
import I2 from '../assets/invoices-2.png';
import I3 from '../assets/invoices-3.png';
import I4 from '../assets/invoices-4.png';
import I5 from '../assets/invoices-5.png';
import GB1 from '../assets/guest-booking-1.png';
import GB2 from '../assets/guest-booking-2.png';
import GB3 from '../assets/guest-booking-3.png';
import ES1 from '../assets/estimate-1.png';
import ES2 from '../assets/estimate-2.png';
import ES3 from '../assets/estimate-3.png';
import ES4 from '../assets/estimate-4.png';
import GD1 from '../assets/garage-desktop-1.png';
import GD2 from '../assets/garage-desktop-2.png';
import GD3 from '../assets/garage-desktop-3.png';
import GD4 from '../assets/garage-desktop-4.png';

interface Instruction {
  text: string;
  subItems?: string[];
  image: string;
}

interface Step {
  title: string;
  instructions: Instruction[];
}

const steps: Step[] = [
  {
    title: 'Create your account',
    instructions: [
      { text: 'Download the Sparki Garage app from your App Store.', image: GS1 },
      { text: 'Create your account and verify your details.', image: GS2 },
      { text: 'Add your garage information.', image: GS3 }
    ],
  },
  {
    title: 'Set up MOT',
    instructions: [
      { text: 'From Home, tap Manage MOTs.', image: MOT1 },
      { text: 'Enter your price and toggle “Do you want to offer MOTs”.', image: MOT2 },
      {
        text: 'Choose how you manage slots',
        subItems: [
          'Leave toggle off for appointment slots throughout the day (e.g. appointments every 30 minutes for 2 cars)',
          'Turn toggle on for drop-off windows (.e.g 8:00-9:30am) - you can send a notification when the car is ready',
        ],
        image: MOT3,
      },
      { text: 'Tap Manage Availability to add slots or windows.', image: MOT4 },
    ],
  },
  {
    title: 'Add your services & repairs',
    instructions: [
      { text: 'Tap Manage Services to add services (e.g. Oil & Filter).', image: SR1 },
      { text: 'Tap Manage Repairs & Quotes to set up repair workflows.', image: SR2 },
      {
        text: 'Choose how you manage slots',
        subItems: [
          'Leave toggle off for slots throughout the day (e.g. 2 cars every 30 minutes)',
          'Turn toggle on for drop-off windows (e.g. 8:00–9:30 am)',
        ],
        image: SR3,
      },
      {
        text: 'Decide availability:',
        subItems: [
          'You can choose shared availabiltity for services, or individually setting availability for each service',
          'You can choose shared availabiltity for repairs & quotes, or set availability for quotes and individually set availability for each repair',
        ],
        image: SR4,
      },
      { text: 'See Step 5 Manage Availability below for more guidance on setting your availability.', image: SR5 },
    ],
  },
  {
    title: 'Managing availability',
    instructions: [
      { text: 'Click Add Appointments to set availability', image: MA1 },
      { text: 'Choose your days and slots or windows. For drop off windows, if you want multiple windows on the same day, just add each set seperately.',
        subItems: [
          'For slots throughout the day, pick your start and end time and how often you want the slots. If you want a break between slots, just add each set seperately',
          'For drop off windows, if you want multiple windows on the same day, just add each set seperately.',
        ],
        image: MA2
      },
    ],
  },
  {
    title: 'Set up optional extras',
    instructions: [
      { text: 'From Home, tap Manage Extras.', subItems: ['Extras auto-appear as optional add-ons during booking'], image: EX1 },
      { text: 'Tap + Add extra, enter name, price & VAT.', image: EX2 },
    ],
  },
  {
    title: 'Add additional users',
    instructions: [
      { text: 'Tap Manage Colleagues on Home.', image: AU1 },
      { text: 'Enter teammate’s email to send invite.', image: AU2 }
    ],
  },
  {
    title: 'Promote the app and add web booking',
    instructions: [
      { text: 'On desktop, visit garage.sparkiapp.com & log in.', image: GD1 },
      { text: 'In the Marketing panel on the left, download posters which will include a link to your app.', image: GD2 },
      {
        text: 'You can also get your guest booking URL which lets anyone book directly from your website.',
        subItems: [
          'Add this link to your website and customers can book without logging in or downloading the app (including all the MOTs and services youve set up',
          'You can also use the ready made HTML button if your site supports it (click Copy snippet).'
        ],
        image: GD3
      },
      { text: 'Add the app link to your website or share on social media to let customers know to download the app to make direct enquiries, receive quotes and make bookings', image: GD4 },
    ],
  },
  {
    title: 'Manage bookings & communicate',
    instructions: [
      { text: 'Tap Schedule view today’s appointments.', image: MB1 },
      { text: 'To view all appointment, tap Bookings', image: MB2 },
      {
        text: 'Open a booking to:',
        subItems: [
          'Request changes for app users',
          'Edit or cancel guest bookings',
          'Check their car technical info, MOT and tax status',
          'Update or send their invoice'
        ],
        image: MB3,
      },
      { text: 'On the day you can mark as Ready to Collect to send them a notification', image: MB4 },
      { text: 'If they booked using the app, you can Message them directly', image: MB5 },
    ],
  },
  {
    title: 'Assign bookings to colleagues',
    instructions: [
      { text: 'Tap Manage Colleagues on Home.', image: MC1 },
      { text: 'Add each colleague.', image: MC2 },
      {
        text: 'In each booking, click Assign',
        subItems: ['You can filter todays Schedule by colleague'],
        image: MC3
      },
    ],
  },
  {
    title: 'Send and manage invoices & track payments',
    instructions: [
      { text: 'On any appointment page you can add invoice items.', image: I1 },
      { text: 'Choose the invoice item category, amount and description then click Save.', image: I2 },
      { text: 'Click on any item to edit or delete it.', image: I3 },
      { text: 'Tap Email invoice to issue the current invoice summary to your customer.', image: I4 },
      { text: 'Once paid, use the Mark as Paid toggle to issue a payment confirmation.', image: I5 }
    ],
  },
  {
    title: 'Export an invoice summary',
    instructions: [
      { text: 'Click View Paid Invoice Summary to view your invoices', image: IS1 },
      { text: 'Choose any date range to view all invoices between those dates', image: IS2 },
      { text: 'Click Email me summary and you will receive a CSV summary including for QuickBooks and Xero (between the dates selected)', image: IS3 },
    ],
  },
  {
    title: 'Book guest appointments',
    instructions: [
      { text: 'Click Book guest appointments to book an appointment for someone who doesnt have the app', image: GB1 },
      { text: 'Choose what type of booking', image: GB2 },
      {
        text: 'Add all the required details and click a slot to book',
        subItems: [
          'Your customer will be sent an email confirming their booking',
          'If you want to view all slots you have set up, you can ignore current bookings'
        ],
        image: GB3
      },
    ],
  },
  {
    title: 'Send a quote or estimate',
    instructions: [
      { text: 'If a customer has enquired through the app, you can respond to their enquiry with a quote', image: ES1 },
      { text: 'You can also provide a quote at any time by using the buttons at the top of the chat screen', image: ES2 },
      { text: 'If you want to send an estimate to someone who doesnt have the app, tap Send guest estimate on the home page ', image: ES3 },
      {
        text: 'Add the details required and invoice items, then tap Create (see instructions above on adding or editing an invoice)',
        subItems: ['Customers can accept quotes and book an appointment straight away'],
        image: ES4
      },
    ],
  },
];

const GetStartedPage: React.FC = () => {
  const [selectedStep, setSelectedStep] = useState(0);
  const [selectedInstr, setSelectedInstr] = useState(0);

  const [downloadPlatform, setDownloadPlatform] = useState<'ios' | 'android'>('ios');

  const garageIosUrl = process.env.REACT_APP_GARAGE_IOS_STORE_URL || '';
  const garageAndroidUrl = process.env.REACT_APP_GARAGE_ANDROID_STORE_URL || '';
  const activeDownloadUrl = downloadPlatform === 'ios' ? garageIosUrl : garageAndroidUrl;

  const currentInstr = steps[selectedStep].instructions[selectedInstr];

  // indices mapped to keep selection stable
  const quickStart = useMemo(() => (
    steps.slice(0, 3).map((s, i) => ({ step: s, idx: i }))
  ), []);

  const marketingIdx = useMemo(
    () => steps.findIndex(s => s.title === 'Promote the app and add web booking'),
    []
  );

  const marketing = useMemo(() => (
    marketingIdx >= 0 ? { step: steps[marketingIdx], idx: marketingIdx } : null
  ), [marketingIdx]);

  // everything after the first 3, excluding the marketing step
  const moreToDo = useMemo(() => (
    steps
      .map((s, i) => ({ step: s, idx: i }))
      .filter(({ idx }) => idx >= 3 && idx !== marketingIdx)
  ), [marketingIdx]);

  const renderStep = (payload: { step: Step; idx: number; displayNum: number }) => {
  const { step, idx, displayNum } = payload;
  const isStepActive = idx === selectedStep;

  return (
    <div key={idx} className={`step-item ${isStepActive ? 'active' : ''}`}>
      <h3 style={{ margin: '0 0 0.5rem', textTransform: 'none' }}>
        {displayNum}. {step.title}
      </h3>
      <ul>
        {idx === 0 && (
          <div
            style={{
              background: isStepActive ? 'rgba(255,255,255,0.14)' : '#fff',
              border: isStepActive ? '1px solid rgba(255,255,255,0.35)' : '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              margin: '0 0 1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '0.75rem',
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStep(idx);
                  setSelectedInstr(0);
                  setDownloadPlatform('ios');
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 999,
                  padding: '0.45rem 0.65rem',
                  cursor: 'pointer',
                  background: downloadPlatform === 'ios' ? '#20B2AA' : (isStepActive ? 'rgba(255,255,255,0.18)' : '#F3F3F3'),
                  color: downloadPlatform === 'ios' ? '#fff' : (isStepActive ? '#fff' : 'var(--text-light)'),
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                iPhone
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStep(idx);
                  setSelectedInstr(0);
                  setDownloadPlatform('android');
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 999,
                  padding: '0.45rem 0.65rem',
                  cursor: 'pointer',
                  background: downloadPlatform === 'android' ? '#20B2AA' : (isStepActive ? 'rgba(255,255,255,0.18)' : '#F3F3F3'),
                  color: downloadPlatform === 'android' ? '#fff' : (isStepActive ? '#fff' : 'var(--text-light)'),
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Android
              </button>
            </div>

            {activeDownloadUrl ? (
              <a
                href={activeDownloadUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  textDecoration: 'none',
                  color: isStepActive ? '#fff' : 'var(--text-light)',
                }}
              >
                <span
                  style={{
                    background: '#fff',
                    borderRadius: 10,
                    padding: 8,
                    display: 'inline-flex',
                    lineHeight: 0,
                  }}
                >
                  <QRCodeSVG value={activeDownloadUrl} size={82} />
                </span>
                <span style={{ fontSize: '0.92rem', lineHeight: 1.35 }}>
                  Scan to download the Sparki Garage app for {downloadPlatform === 'ios' ? 'iPhone' : 'Android'}.
                </span>
              </a>
            ) : (
              <p
                style={{
                  margin: 0,
                  color: isStepActive ? '#fff' : 'var(--text-light)',
                  fontSize: '0.92rem',
                  lineHeight: 1.35,
                }}
              >
                Add the {downloadPlatform === 'ios' ? 'App Store' : 'Google Play'} URL to the site environment variables to show the QR code.
              </p>
            )}
          </div>
        )}
        {step.instructions.map((instr, i) => {
          const isInstrActive = isStepActive && i === selectedInstr;
          return (
            <li
              key={i}
              onClick={() => {
                setSelectedStep(idx);
                setSelectedInstr(i);
              }}
              style={{
                cursor: 'pointer',
                fontWeight: isInstrActive ? 600 : 300,
                color: isStepActive ? '#fff' : 'var(--text-light)',
                marginBottom: '.5rem',
              }}
            >
              {instr.text}
              {instr.subItems && (
                <ul className="sub-list">
                  {instr.subItems.map((sub, j) => (
                    <li
                      key={j}
                      style={{
                        color: isStepActive ? '#fff' : 'var(--text-light)',
                        fontWeight: 300,
                      }}
                    >
                      {sub}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

  return (
    <div className="get-started-container">
      <div className="steps-list">
        <Link to="/" className="back-home-link">← Back to home</Link>
        <h1 style={{ margin: '1rem 0', textTransform: 'none' }}>Get started</h1>
        <p className="step-hint desktop-only">Click any step below to preview it in the phone frame</p>
        <p className="step-hint mobile-only">Tap any step below to preview it in the phone frame</p>

        <h2 style={{ color: 'var(--primary)', margin: '1.5rem 0 1.2rem', textTransform: 'none' }}>
          Your first 3 steps
        </h2>
        {quickStart.map((p, i) => renderStep({ ...p, displayNum: i + 1 }))}

        {marketing && (
          <>
            <h2 style={{ color: 'var(--primary)', margin: '1.5rem 0 1.2rem', textTransform: 'none' }}>
              Grow your bookings
            </h2>
            {renderStep({ ...marketing, displayNum: quickStart.length + 1 })}
              </>
            )}
            <h2 style={{ color: 'var(--primary)', margin: '1.5rem 0 1.2rem', textTransform: 'none' }}>
              More you can do
            </h2>
            {moreToDo.map((p, i) =>
              renderStep({ ...p, displayNum: quickStart.length + (marketing ? 2 : 1) + i })
            )}
      </div>

      <div className="preview-panel">
        <div className="phone-mockup">
          <img
            src={currentInstr.image}
            alt={`Step ${selectedStep + 1}, instruction ${selectedInstr + 1}`}
            className="phone-screenshot-image"
          />
          <img
            src={phoneFrame}
            alt="Phone frame"
            className="phone-frame-image"
          />
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;
